import { useQuery } from '@tanstack/react-query'
import { Edit, Info, Ticket, Trash } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  createTituloPremiado,
  deleteTituloPremiado,
  getAllTitulosPremiados,
  getTituloPremiadoById,
  type ICreateTituloPremiado,
  type ITituloPremiado,
  updateTituloPremiado,
} from '@/api/campaigns/titulo'
import type { GetProfileResponse } from '@/api/user/get-profile'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { api } from '@/services/axios'

import { TituloPremiadoModal } from './tituloPremiadoModal'

interface Ganhador {
  id: number
  titulo: string
  premio: string
  numeroSorteado?: string
  nomeComprador?: string
  whatsappComprador?: string
  emailComprador?: string
  usuario_Ganhador_Id?: number
}

export function TituloPremiadoCRUD({ campanhaId }: { campanhaId: number }) {
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isGanhadorModalOpen, setIsGanhadorModalOpen] = useState(false)
  const [currentTitulo, setCurrentTitulo] = useState<ITituloPremiado | null>(
    null,
  )
  const [ganhadorSelecionado, setGanhadorSelecionado] =
    useState<Ganhador | null>(null)

  const getPurchasedNumbers = async (): Promise<number[]> => {
    const { data } = await api.get<number[]>(
      `CampanhaPremio/GetAllPurchasedNumbers/${campanhaId}`,
    )
    return data
  }

  const { data: titulos, refetch } = useQuery({
    queryKey: ['titulos-premiados', campanhaId],
    queryFn: () => getAllTitulosPremiados(campanhaId),
  })

  const filteredTitulos = titulos?.filter(
    (titulo: Ganhador) =>
      titulo.titulo.toLowerCase().includes(search.toLowerCase()) ||
      titulo.premio.toLowerCase().includes(search.toLowerCase()),
  )

  const handleCreateOrUpdate = async (data: ICreateTituloPremiado) => {
    try {
      if (currentTitulo) {
        await updateTituloPremiado(currentTitulo.id, data)
        toast.success('Título premiado atualizado com sucesso!')
      } else {
        await createTituloPremiado({ ...data, campanha_id: campanhaId })
        toast.success('Título premiado criado com sucesso!')
      }
      refetch()
      setIsModalOpen(false)
      setCurrentTitulo(null)
    } catch (error) {
      toast.error('Erro ao salvar título premiado')
    }
  }

  const handleEdit = async (id: number) => {
    const titulo = titulos?.find((t: any) => t.id === id)
    if (!titulo) {
      toast.error('Título não encontrado.')
      return
    }

    const numero = Number(titulo.titulo)
    const purchased = await getPurchasedNumbers()

    if (purchased.includes(numero)) {
      toast.error('Não é possível editar: esse número já foi vendido.')
      return
    }

    const tituloCompleto = await getTituloPremiadoById(id)
    setCurrentTitulo(tituloCompleto)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      const titulo = titulos?.find((t: any) => t.id === id)
      if (!titulo) return toast.error('Título não encontrado.')

      const numero = Number(titulo.titulo)
      const purchased = await getPurchasedNumbers()

      if (purchased.includes(numero)) {
        toast.error('Não é possível excluir: esse número já foi vendido.')
        return
      }

      await deleteTituloPremiado(id)
      toast.success('Título premiado excluído com sucesso!')
      refetch()
    } catch (error) {
      toast.error('Erro ao excluir título premiado')
    }
  }

  const handleShowGanhador = async (titulo: Ganhador) => {
    if (titulo.usuario_Ganhador_Id !== null) {
      const { data } = await api.get<GetProfileResponse>(
        `Usuario/GetById/${titulo.usuario_Ganhador_Id}`,
      )

      setGanhadorSelecionado({
        ...titulo,
        numeroSorteado: titulo.titulo,
        nomeComprador: data.nome,
        whatsappComprador: data.whatsapp,
        emailComprador: data.email,
      })
      setIsGanhadorModalOpen(true)
    }
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-md">
          <Input
            placeholder="Buscar títulos premiados..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto"
        >
          <Ticket className="mr-2 h-4 w-4" />
          Adicionar Título Premiado
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <div className="min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%] text-center">Nº Título</TableHead>
                <TableHead className="w-[30%] text-center">
                  Valor do Prêmio
                </TableHead>
                <TableHead className="w-[20%] text-center">Vencedor</TableHead>
                <TableHead className="w-[20%] text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTitulos?.length ? (
                filteredTitulos.map((titulo: Ganhador) => (
                  <TableRow key={titulo.id}>
                    <TableCell className="text-center font-medium">
                      {titulo.titulo}
                    </TableCell>
                    <TableCell className="text-center">
                      {titulo.premio}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShowGanhador(titulo)}
                        className="text-green-500 hover:text-green-700"
                      >
                        <Info className="h-4 w-4" />
                        <span className="ml-2"></span>
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="grid justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(titulo.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(titulo.id)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    Nenhum título premiado encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <TituloPremiadoModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleCreateOrUpdate}
        titulo={currentTitulo}
        campanhaId={campanhaId}
      />

      {/* Modal do Ganhador */}
      <Dialog open={isGanhadorModalOpen} onOpenChange={setIsGanhadorModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Detalhes do Ganhador</DialogTitle>
            <DialogDescription>
              Informações para que você entre em contato com o vencedor.
            </DialogDescription>
          </DialogHeader>
          {ganhadorSelecionado && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Prêmio:</p>
                <p className="col-span-3 text-sm font-semibold">
                  {ganhadorSelecionado.premio}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Número:</p>
                <p className="col-span-3 text-sm font-semibold">
                  #{ganhadorSelecionado.numeroSorteado}
                </p>
              </div>
              <Separator />
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Nome:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.nomeComprador}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Telefone:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.whatsappComprador}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Email:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.emailComprador}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
